import { EventFeedTeaseProps } from "design-system/components/primitives/event-feed-tease/event-feed-tease";
import { SearchDisplay } from "frontend/types";
import { EventsTeaseItemProps } from "design-system/components/blocks/events-tease/events-tease-item";

export function createEventFeedTeaseProps(
  data: SearchDisplay,
): EventFeedTeaseProps {
  const props: EventFeedTeaseProps = {
    type: "Event",
    title: data.title,
    href: data.display?.eventLink,
    dates: [],
    overline: data.display?.overline?.label,
    meta: [],
    featuredImage: undefined,
  };

  const [start, end] = data.display?.dateRange?.map(String) ?? [];
  props.dates =
    start && end
      ? start === end
        ? [start]
        : [start, end]
      : start
        ? [start]
        : [];

  props.meta = data.display?.eventMeta;

  if (data.display?.thumbnail) {
    props.featuredImage = {
      src: data.display.thumbnail.src,
      alt: data.display.thumbnail.alt,
    };
  }

  if (data.description) {
    props.blurb = data.description;
  }

  return props;
}

function parseTimeRange(timeString: string): [string, string] | string {
  const parts = timeString.split(" - ");
  if (parts.length === 2) {
    // If there are two parts, it's a range
    return parts.map(parseTime) as [string, string];
  } else if (parts.length === 1) {
    // If there's only one part, it's a single time
    const parsedTime = parseTime(parts[0] ?? "");
    return parsedTime;
  }
  return timeString;
}

function parseTime(time: string): string {
  const date = new Date();
  const [hours, minutes] = time.split(":").map((part) => part.trim());
  const isPM = time.toLowerCase().includes("pm");
  if (hours && minutes) {
    const parsedHours = parseInt(hours);
    const adjustedHours =
      isPM && parsedHours !== 12 ? parsedHours + 12 : parsedHours;
    date.setHours(adjustedHours, parseInt(minutes));
    return date.toISOString();
  }
  return time; // Return original time if parsing fails
}

function getEventTime(
  eventMeta: Array<Array<{ title?: string; info?: string }>> | undefined,
): string | [string, string] | undefined {
  if (!eventMeta) return undefined;

  for (const metaGroup of eventMeta) {
    const timeObject = metaGroup.find(
      (item) => item.title?.toLowerCase() === "times",
    );
    if (timeObject?.info) {
      return parseTimeRange(timeObject.info);
    }
  }
  return undefined;
}

export function createEventTeaseProps(
  data: SearchDisplay,
): EventsTeaseItemProps {
  const props: EventsTeaseItemProps = {
    event: {
      title: data.title,
      location: data.location ?? "",
      date: "",
      href: data.display?.eventLink,
      featuredImage: data.display?.thumbnail
        ? {
            src: data.display.thumbnail.src,
            alt: data.display.thumbnail.alt,
          }
        : undefined,
    },
  };
  const [start, end] = data.display?.dateRange?.map(String) ?? [];
  props.event.date =
    start && end ? (start === end ? start : [start, end]) : start ? start : "";

  const eventTime = getEventTime(data.display?.eventMeta);
  props.event.time = eventTime;

  return props;
}
